import { useTranslation } from 'react-i18next'
import { useLoaderData, useNavigate, useParams } from 'react-router-dom'

import { StyledActionButton } from '@/components/Buttons.tsx'
import { Confirm } from '@/components/templates/Confirm.tsx'
import { FormStyled, FormSubmit } from '@/components/templates/Form.tsx'
import { ScrollableContent } from '@/components/templates/ScreenLayout.tsx'
import { ScreenWrapper } from '@/components/templates/ScreenWrapper.tsx'
import { Description, Header1 } from '@/components/Text.tsx'
import { useTrackFormProgress } from '@/hooks/useTrackFormProgress.ts'
import {
  calculateBMIPath,
  patientDetailsPath,
  selectDateTimePath,
} from '@/router/paths.ts'
import { useAppointmentStore } from '@/store.ts'
import { showBMIForCategory } from '@/utils/showBMIForCategory.ts'
import { VelthuisFormStep } from '@/utils/VelthuisFormSteps.ts'

import { LoaderData } from './actions.ts'

const ProposedAppointment = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'proposedAppointment',
  })
  const navigate = useNavigate()
  const { appointmentType, category, doctorId, locationId } = useParams() as {
    appointmentType: string
    category: string
    locationId: string
    doctorId: string
  }
  const selectedSlot = useAppointmentStore((state) => state.selectedSlot)
  const { loadTime } = useLoaderData() as LoaderData

  const { trackFormInteraction } = useTrackFormProgress(
    VelthuisFormStep.ProposedAppointment,
    { loadTime },
  )

  if (!selectedSlot) {
    return null
  }

  return (
    <ScreenWrapper>
      <FormStyled method="post">
        <Header1>{t('header')}</Header1>
        <Description>{t('description')}</Description>

        <ScrollableContent>
          <Confirm slot={selectedSlot} />
        </ScrollableContent>

        <FormSubmit>
          <StyledActionButton
            variant="Primary"
            onClick={(e) => {
              e.preventDefault()
              if (showBMIForCategory(category)) {
                navigate(
                  calculateBMIPath({
                    appointmentType,
                    category,
                    doctorId,
                    locationId,
                  }),
                )
              } else {
                navigate(patientDetailsPath())
              }
              trackFormInteraction({
                action: 'next',
                formFieldName: 'confirm-appointment',
              })
            }}
          >
            {t('actions.confirmAppointment')}
          </StyledActionButton>

          <StyledActionButton
            variant="Secondary"
            onClick={(e) => {
              e.preventDefault()
              navigate(
                selectDateTimePath({
                  appointmentType,
                  category,
                  doctorId,
                  locationId,
                }),
              )
              trackFormInteraction({
                action: 'previous',
                formFieldName: 'back-to-previous',
              })
            }}
          >
            {t('actions.selectTimeSlot')}
          </StyledActionButton>
        </FormSubmit>
      </FormStyled>
    </ScreenWrapper>
  )
}

export { ProposedAppointment }
